import { buildTopologySubEntityId } from '../../../../../topology/TopologyReferenceBuilders';
/**
 * 稳定拓扑匹配支持的子拓扑类型。
 */
export type BlendStableTopologyKind = Extract<TopologySubEntityKind, 'face' | 'vertex'>;

/**
 * 面/顶点运行时拓扑提示。
 */
export interface BlendStableTopologyRuntimeHint {
  /** 提示类型。 */
  kind: BlendStableTopologyKind;
  /** 来源实体对象 ID。 */
  sourceObjectId: string;
  /** 当前权威快照中的局部索引。 */
  topologyIndex: number;
  /** 运行时持久化引用。 */
  persistentRef: string;
  /** 运行时权威子拓扑 ID。 */
  subEntityId?: string;
  /** Puppy CAD 内核 shape 哈希。 */
  shapeHash?: number;
}

/**
 * 面/顶点选择输入。
 */
export interface BlendStableTopologySelection {
  /** 选择项类型。 */
  kind: BlendStableTopologyKind;
  /** 选择项权威子拓扑 ID。 */
  subEntityId?: string;
  /** 选择项持久化引用。 */
  persistentRef: string;
  /** 运行时拓扑提示。 */
  runtimeTopologyHint?: BlendStableTopologyRuntimeHint | null;
}

/**
 * 面/顶点候选快照。
 */
export interface BlendStableTopologyCandidate {
  /** Puppy CAD 内核 枚举得到的实际 shape 索引。 */
  shapeIndex: number;
  /** 权威拓扑索引；无出处表时回退为 shapeIndex。 */
  topologyIndex: number | null;
  /** 权威子拓扑 ID。 */
  subEntityId?: string | null;
  /** 出处表中的持久化引用。 */
  provenancePersistentRef: string | null;
  /** 当前几何可重新计算出的持久化引用。 */
  geometryPersistentRef: string | null;
  /** Puppy CAD 内核 shape 哈希。 */
  shapeHash?: number;
}

/**
 * 面/顶点匹配输入。
 */
export interface BlendStableTopologyMatchInput {
  /** 子拓扑类型。 */
  kind: BlendStableTopologyKind;
  /** 来源实体对象 ID。 */
  sourceObjectId?: string;
  /** 选择输入。 */
  selection: BlendStableTopologySelection;
  /** 当前源体候选快照。 */
  candidates: BlendStableTopologyCandidate[];
}

/**
 * 面/顶点稳定拓扑匹配器。
 *
 * 设计说明：
 * 1. 面圆角、面面倒角、顶点倒角都依赖“用户选中的上游子拓扑”；
 * 2. 草图尺寸改变后，旧的几何引用可能失效，但 `subEntityId` 与出处表索引仍是重建主链路；
 * 3. 匹配优先级集中在这个类中，避免各个 solver 用局部回退逻辑把拓扑真相打散。
 */
export class BlendStableTopologyMatcher {
  /**
   * 解析目标 shape 索引。
   *
   * @param input 匹配输入。
   * @returns Puppy CAD 内核 shape 数组索引。
   */
  public resolve(input: BlendStableTopologyMatchInput): number {
    const authoritativeSubEntityId = (
      input.selection.subEntityId
      ?? input.selection.runtimeTopologyHint?.subEntityId
    );
    const strongSubEntityId = this.resolveStrongSubEntityId(
      input.kind,
      authoritativeSubEntityId,
      input.selection,
    );
    const exactSubEntityMatches = this.filterBySubEntityId(input.candidates, strongSubEntityId);
    if (exactSubEntityMatches.length === 1) {
      return exactSubEntityMatches[0]!.shapeIndex;
    }
    this.assertAuthoritativeSubEntityIsResolvable(
      input.candidates,
      strongSubEntityId,
      exactSubEntityMatches.length,
      input.kind,
    );

    const runtimeMatch = this.resolveRuntimeHintMatch(input);
    if (runtimeMatch !== null) {
      return runtimeMatch;
    }

    const exactProvenanceMatches = input.candidates.filter((candidate) => (
      candidate.provenancePersistentRef === input.selection.persistentRef
    ));
    if (exactProvenanceMatches.length === 1) {
      return exactProvenanceMatches[0]!.shapeIndex;
    }

    const exactGeometryMatches = input.candidates.filter((candidate) => (
      candidate.geometryPersistentRef === input.selection.persistentRef
    ));
    if (exactGeometryMatches.length === 1) {
      return exactGeometryMatches[0]!.shapeIndex;
    }

    if (exactProvenanceMatches.length > 1 || exactGeometryMatches.length > 1) {
      throw new Error(`${this.resolveKindLabel(input.kind)}拓扑引用匹配存在歧义: ${input.selection.persistentRef}`);
    }

    throw new Error(`未找到${this.resolveKindLabel(input.kind)}拓扑引用: ${input.selection.persistentRef}`);
  }

  /**
   * 使用运行时提示解析候选。
   *
   * @param input 匹配输入。
   * @returns 命中的 shape 索引；无法证明同源时返回 null。
   */
  private resolveRuntimeHintMatch(input: BlendStableTopologyMatchInput): number | null {
    const runtimeHint = input.selection.runtimeTopologyHint;
    if (!runtimeHint || runtimeHint.kind !== input.kind) {
      return null;
    }
    if (
      typeof input.sourceObjectId === 'string'
      && runtimeHint.sourceObjectId !== input.sourceObjectId
    ) {
      return null;
    }

    const exactSubEntityMatches = this.filterBySubEntityId(input.candidates, runtimeHint.subEntityId);
    if (exactSubEntityMatches.length === 1) {
      return exactSubEntityMatches[0]!.shapeIndex;
    }

    const exactPersistentMatches = this.filterByPersistentRef(input.candidates, runtimeHint.persistentRef);
    if (exactPersistentMatches.length === 1) {
      return exactPersistentMatches[0]!.shapeIndex;
    }

    const topologyIndexMatch = this.resolveTopologyIndexMatch(input.candidates, runtimeHint);
    if (topologyIndexMatch !== null) {
      return topologyIndexMatch;
    }

    return null;
  }

  /**
   * 在运行时提示同源时尝试使用 topologyIndex 回绑。
   *
   * @param candidates 候选集合。
   * @param runtimeHint 运行时提示。
   * @returns 命中的 shape 索引；无法证明时返回 null。
   */
  private resolveTopologyIndexMatch(
    candidates: BlendStableTopologyCandidate[],
    runtimeHint: BlendStableTopologyRuntimeHint,
  ): number | null {
    if (!Number.isInteger(runtimeHint.topologyIndex)) {
      return null;
    }
    const candidate = candidates.find((item) => (
      item.topologyIndex === runtimeHint.topologyIndex
      || item.shapeIndex === runtimeHint.topologyIndex
    )) ?? null;
    if (!candidate) {
      return null;
    }

    if (this.filterByPersistentRef([candidate], runtimeHint.persistentRef).length === 1) {
      return candidate.shapeIndex;
    }
    if (
      Number.isInteger(runtimeHint.shapeHash)
      && candidate.shapeHash === runtimeHint.shapeHash
    ) {
      return candidate.shapeIndex;
    }

    /**
     * 当当前候选没有任何出处表身份时，说明这是旧数据或测试环境的轻量快照。
     * 此时 topologyIndex 是唯一同源线索，允许回退，但一旦候选具备权威 ID，
     * 必须由 ID 或 ref 自证，避免把不相关的同索引子拓扑误认回来。
     */
    if (!this.hasAuthoritativeCandidateIdentity(candidates)) {
      return candidate.shapeIndex;
    }

    return null;
  }

  /**
   * 按持久化引用过滤候选。
   *
   * @param candidates 候选集合。
   * @param persistentRef 目标持久化引用。
   * @returns 命中候选。
   */
  private filterByPersistentRef(
    candidates: BlendStableTopologyCandidate[],
    persistentRef: string,
  ): BlendStableTopologyCandidate[] {
    return candidates.filter((candidate) => (
      candidate.provenancePersistentRef === persistentRef
      || candidate.geometryPersistentRef === persistentRef
    ));
  }

  /**
   * 按权威子拓扑 ID 过滤候选。
   *
   * @param candidates 候选集合。
   * @param subEntityId 目标子拓扑 ID。
   * @returns 命中候选。
   */
  private filterBySubEntityId(
    candidates: BlendStableTopologyCandidate[],
    subEntityId: string | null | undefined,
  ): BlendStableTopologyCandidate[] {
    if (typeof subEntityId !== 'string' || subEntityId.length <= 0) {
      return [];
    }
    return candidates.filter((candidate) => candidate.subEntityId === subEntityId);
  }

  /**
   * 解析应当强制命中的权威 ID。
   *
   * @param kind 子拓扑类型。
   * @param subEntityId 输入子拓扑 ID。
   * @param selection 选择输入。
   * @returns 强权威 ID；几何派生 ID 返回 null。
   */
  private resolveStrongSubEntityId(
    kind: BlendStableTopologyKind,
    subEntityId: string | null | undefined,
    selection: BlendStableTopologySelection,
  ): string | null {
    if (typeof subEntityId !== 'string' || subEntityId.length <= 0) {
      return null;
    }
    const persistentRefs = [
      selection.persistentRef,
      selection.runtimeTopologyHint?.persistentRef,
    ].filter((persistentRef): persistentRef is string => (
      typeof persistentRef === 'string' && persistentRef.length > 0
    ));
    const weakSubEntityIds = persistentRefs.flatMap((persistentRef) => [
      persistentRef,
      buildTopologySubEntityId(kind, persistentRef),
    ]);
    return weakSubEntityIds.includes(subEntityId) ? null : subEntityId;
  }

  /**
   * 断言权威 ID 未命中时是否允许继续回退。
   *
   * @param candidates 候选集合。
   * @param subEntityId 强权威 ID。
   * @param matchedCount 已命中的数量。
   * @param kind 子拓扑类型。
   */
  private assertAuthoritativeSubEntityIsResolvable(
    candidates: BlendStableTopologyCandidate[],
    subEntityId: string | null | undefined,
    matchedCount: number,
    kind: BlendStableTopologyKind,
  ): void {
    if (typeof subEntityId !== 'string' || subEntityId.length <= 0) {
      return;
    }
    if (matchedCount > 1) {
      throw new Error(`${this.resolveKindLabel(kind)}权威拓扑身份匹配存在歧义: ${subEntityId}`);
    }
    if (this.hasAuthoritativeCandidateIdentity(candidates)) {
      throw new Error(`未找到${this.resolveKindLabel(kind)}权威拓扑身份: ${subEntityId}`);
    }
  }

  /**
   * 判断候选是否携带权威身份。
   *
   * @param candidates 候选集合。
   * @returns 是否存在权威身份。
   */
  private hasAuthoritativeCandidateIdentity(candidates: BlendStableTopologyCandidate[]): boolean {
    return candidates.some((candidate) => (
      typeof candidate.subEntityId === 'string'
      && candidate.subEntityId.length > 0
    ));
  }

  /**
   * 解析用户可读的拓扑类型标签。
   *
   * @param kind 子拓扑类型。
   * @returns 类型标签。
   */
  private resolveKindLabel(kind: BlendStableTopologyKind): string {
    return kind === 'face' ? '面' : '顶点';
  }
}
